import { useState } from 'react';
import { ArrowLeft, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnersPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    interest: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(''); // Clear error on change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      await axios.post(`${API}/partners/inquiries`, formData);
      setIsSubmitted(true);
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="partners-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-[#E8E3D8]" data-testid="navigation">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <a href="https://temporarysatisfaction.com" className="font-heading text-xl sm:text-2xl font-semibold text-[#2A2A2A] hover:text-[#5C5C5A] transition-colors" data-testid="nav-logo">
              Temporary Satisfaction
            </a>
            <div className="hidden md:flex items-center gap-8">
              <a href="https://temporarysatisfaction.com" className="text-sm font-medium text-[#5C5C5A] hover:text-[#2A2A2A] transition-colors" data-testid="nav-main-site">Main Site</a>
              <a href="/technology" className="text-sm font-medium text-[#5C5C5A] hover:text-[#2A2A2A] transition-colors" data-testid="nav-platform">Platform</a>
              <a href="/mystoryworld" className="text-sm font-medium text-[#5C5C5A] hover:text-[#2A2A2A] transition-colors" data-testid="nav-mystoryworld">MyStoryWorld</a>
              <a href="/partners" className="text-sm font-medium text-[#2A2A2A] transition-colors" data-testid="nav-partners">Partner With Us</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          {/* Back Link */}
          <a 
            href="/technology" 
            className="inline-flex items-center gap-2 text-sm text-[#5C5C5A] hover:text-[#2A2A2A] transition-colors mb-8"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Platform
          </a>

          {!isSubmitted ? (
            <>
              {/* Header */}
              <div className="mb-12">
                <span className="overline block mb-4" data-testid="partners-overline">Partner With Us</span>
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#2A2A2A] mb-6" data-testid="partners-title">
                  Let's Build Something Meaningful Together
                </h1>
                <p className="text-lg text-[#5C5C5A] leading-relaxed" data-testid="partners-description">
                  Whether you're a grant funder, community partner, pilot organization, or aligned enterprise — we'd love to explore how our platform can support your mission.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="partner-form">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#2A2A2A] mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-[#E8E3D8] bg-white text-[#2A2A2A] placeholder-[#9A9A98] focus:outline-none focus:ring-2 focus:ring-[#E8D5D1] focus:border-transparent transition-all"
                    placeholder="Jane Smith"
                    data-testid="input-name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#2A2A2A] mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-[#E8E3D8] bg-white text-[#2A2A2A] placeholder-[#9A9A98] focus:outline-none focus:ring-2 focus:ring-[#E8D5D1] focus:border-transparent transition-all"
                    placeholder="jane@organization.org"
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <label htmlFor="organization" className="block text-sm font-medium text-[#2A2A2A] mb-2">
                    Organization
                  </label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-[#E8E3D8] bg-white text-[#2A2A2A] placeholder-[#9A9A98] focus:outline-none focus:ring-2 focus:ring-[#E8D5D1] focus:border-transparent transition-all"
                    placeholder="Your organization or company"
                    data-testid="input-organization"
                  />
                </div>

                <div>
                  <label htmlFor="interest" className="block text-sm font-medium text-[#2A2A2A] mb-2">
                    What are you interested in? *
                  </label>
                  <select
                    id="interest"
                    name="interest"
                    required
                    value={formData.interest}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-[#E8E3D8] bg-white text-[#2A2A2A] focus:outline-none focus:ring-2 focus:ring-[#E8D5D1] focus:border-transparent transition-all appearance-none cursor-pointer"
                    data-testid="select-interest"
                  >
                    <option value="">Select an option</option>
                    <option value="partnership">Partnership Opportunity</option>
                    <option value="pilot">Pilot Program</option>
                    <option value="grant">Grant / Funding Discussion</option>
                    <option value="media">Media / Press Inquiry</option>
                    <option value="general">General Inquiry</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#2A2A2A] mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-[#E8E3D8] bg-white text-[#2A2A2A] placeholder-[#9A9A98] focus:outline-none focus:ring-2 focus:ring-[#E8D5D1] focus:border-transparent transition-all resize-none"
                    placeholder="Tell us about your organization and how you'd like to work together..."
                    data-testid="input-message"
                  />
                </div>

                {error && (
                  <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="form-error">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </Button>
              </form>
            </>
          ) : (
            /* Success State */
            <div className="text-center py-16" data-testid="success-message">
              <div className="w-20 h-20 rounded-full bg-[#C2D1C2]/30 flex items-center justify-center mx-auto mb-8">
                <CheckCircle className="w-10 h-10 text-[#6B8E6B]" strokeWidth={1.5} />
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#2A2A2A] mb-4">
                Thanks for reaching out.
              </h2>
              <p className="text-lg text-[#5C5C5A] mb-4 max-w-md mx-auto">
                Your inquiry has been received. We'll review it and follow up as soon as we can.
              </p>
              <p className="text-sm text-[#5C5C5A] mb-10 max-w-md mx-auto">
                You can return to the main site or continue exploring the platform.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="https://temporarysatisfaction.com" className="btn-secondary inline-flex items-center gap-2" data-testid="back-main-site">
                  Back to Main Site
                </a>
                <a href="/technology" className="btn-primary inline-flex items-center gap-2" data-testid="explore-platform">
                  Explore the Platform
                  <ArrowLeft className="w-4 h-4 rotate-180" />
                </a>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 border-t border-[#E8E3D8]" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <a href="https://temporarysatisfaction.com" className="font-heading text-lg font-semibold text-[#2A2A2A] hover:text-[#5C5C5A] transition-colors">
                Temporary Satisfaction
              </a>
              <a href="https://temporarysatisfaction.com" className="text-sm text-[#5C5C5A] hover:text-[#2A2A2A] transition-colors">
                Return to Temporary Satisfaction
              </a>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-6 border-t border-[#E8E3D8]/50">
              <p className="text-sm text-[#5C5C5A]">Part of the Temporary Satisfaction ecosystem</p>
              <p className="text-sm text-[#5C5C5A]">&copy; {new Date().getFullYear()} Temporary Satisfaction. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PartnersPage;
